import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Loader2, Plus, Trash2, Calculator, Save, Settings2, DollarSign, Sparkles,
} from "lucide-react";
import {
  getQuoteSettings, patchQuoteSettings, listQuotePapers, calculateQuote,
} from "../../../lib/internalQuote";

/* ------------------------------------------------------------------ */
/* Small helpers                                                      */
/* ------------------------------------------------------------------ */
const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;
const money4 = (n) => `$${(Number(n) || 0).toFixed(4)}`;

const Field = ({ label, hint, children }) => (
  <label className="block">
    <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)]">{label}</span>
    <div className="mt-1">{children}</div>
    {hint && <div className="text-[10px] text-[color:var(--ups-subtext)] mt-1">{hint}</div>}
  </label>
);

/* ------------------------------------------------------------------ */
/* Global click-rate settings card                                    */
/* ------------------------------------------------------------------ */
const ClickRateSettings = ({ settings, refresh }) => {
  const [draftBw, setDraftBw] = useState(settings.bw_click_rate);
  const [draftColor, setDraftColor] = useState(settings.color_click_rate);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDraftBw(settings.bw_click_rate);
    setDraftColor(settings.color_click_rate);
  }, [settings.bw_click_rate, settings.color_click_rate]);

  const dirty = Number(draftBw) !== Number(settings.bw_click_rate) || Number(draftColor) !== Number(settings.color_click_rate);

  const save = async () => {
    setSaving(true);
    try {
      await patchQuoteSettings({
        bw_click_rate: Number(draftBw) || 0,
        color_click_rate: Number(draftColor) || 0,
      });
      toast.success("Click rates saved");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save click rates");
    } finally { setSaving(false); }
  };

  return (
    <div className="card-swiss rounded-sm p-4" data-testid="click-rates-card">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.18em] text-[color:var(--ups-subtext)] flex items-center gap-1.5"><Settings2 size={12}/> Global Click Rates</div>
          <div className="text-xs text-[color:var(--ups-subtext)] mt-1">Per side, per parent sheet. Duplex uses 2 clicks per sheet.</div>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="B&W click rate ($)">
          <div className="flex items-center gap-2">
            <DollarSign size={14} className="text-[color:var(--ups-subtext)]" />
            <input type="number" step="0.001" min={0} className="input-swiss w-full px-3 py-2 rounded-sm text-sm font-mono"
              value={draftBw} onChange={(e) => setDraftBw(e.target.value)} data-testid="bw-rate-input" />
          </div>
        </Field>
        <Field label="Color click rate ($)">
          <div className="flex items-center gap-2">
            <DollarSign size={14} className="text-[color:var(--ups-subtext)]" />
            <input type="number" step="0.001" min={0} className="input-swiss w-full px-3 py-2 rounded-sm text-sm font-mono"
              value={draftColor} onChange={(e) => setDraftColor(e.target.value)} data-testid="color-rate-input" />
          </div>
        </Field>
      </div>
      <div className="flex justify-end mt-3">
        <button disabled={!dirty || saving} onClick={save}
          className="btn-primary px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.14em] flex items-center gap-1.5 rounded-sm disabled:opacity-40"
          data-testid="click-rates-save-btn">
          {saving ? <Loader2 size={13} className="animate-spin"/> : <Save size={13}/>} Save Rates
        </button>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Main tab                                                           */
/* ------------------------------------------------------------------ */
const InternalQuoteTab = () => {
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({ bw_click_rate: 0, color_click_rate: 0 });
  const [papers, setPapers] = useState([]);

  // Inputs
  const [copies, setCopies] = useState(100);
  const [sides, setSides] = useState(1);
  const [colorMode, setColorMode] = useState("color");
  const [waste, setWaste] = useState(0);
  const [lines, setLines] = useState([{ material_id: "", ups: 1 }]);

  // Output
  const [result, setResult] = useState(null);
  const [calculating, setCalculating] = useState(false);

  const refresh = async () => {
    try {
      const [s, p] = await Promise.all([getQuoteSettings(), listQuotePapers()]);
      setSettings(s);
      setPapers(p || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load quote data");
    } finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, []);

  const activeClickRate = colorMode === "color" ? settings.color_click_rate : settings.bw_click_rate;

  const addLine = () => setLines((ls) => [...ls, { material_id: "", ups: 1 }]);
  const removeLine = (idx) => setLines((ls) => ls.filter((_, i) => i !== idx));
  const updateLine = (idx, patch) => setLines((ls) => ls.map((l, i) => (i === idx ? { ...l, ...patch } : l)));

  const canCalculate = useMemo(
    () => Number(copies) > 0 && lines.some((l) => l.material_id && Number(l.ups) > 0),
    [copies, lines],
  );

  const calculate = async () => {
    if (!canCalculate) {
      toast.error("Enter copies and pick at least one paper with UPs > 0");
      return;
    }
    setCalculating(true);
    try {
      const payload = {
        copies: Number(copies) || 0,
        sides: Number(sides) || 1,
        color_mode: colorMode,
        waste_sheets: Number(waste) || 0,
        papers: lines
          .filter((l) => l.material_id && Number(l.ups) > 0)
          .map((l) => ({ material_id: l.material_id, ups: Number(l.ups) })),
      };
      setResult(await calculateQuote(payload));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Calculation failed");
    } finally { setCalculating(false); }
  };

  if (loading) return <div className="p-8 text-center"><Loader2 size={24} className="animate-spin inline"/></div>;

  const hasUnpricedPapers = papers.some((p) => p.price_per_sheet === 0);

  return (
    <div className="space-y-6" data-testid="internal-quote-tab">
      <div>
        <h2 className="text-2xl font-bold flex items-center gap-2"><Calculator size={22} className="text-[color:var(--ups-brand)]"/> Internal Quote Calculator</h2>
        <p className="text-sm text-[color:var(--ups-subtext)] mt-1">Paper cost from your Cut Sheet inventory + click charges = printing cost per job. Compare multiple papers side-by-side.</p>
      </div>

      <ClickRateSettings settings={settings} refresh={refresh} />

      {hasUnpricedPapers && (
        <div className="p-3 rounded-sm border border-yellow-500/40 bg-yellow-500/10 text-xs" data-testid="unpriced-warning">
          <b>Heads up:</b> some Cut Sheet papers don&apos;t have a price yet. Set <code>price_per_unit</code> on them in <b>Inventory → Materials</b> so this calculator can price them accurately.
        </div>
      )}

      {/* Inputs card */}
      <div className="card-swiss rounded-sm p-4 space-y-4" data-testid="quote-inputs-card">
        <div className="text-xs font-bold uppercase tracking-[0.18em] text-[color:var(--ups-subtext)]">Job details</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Field label="Copies">
            <input type="number" min={1} className="input-swiss w-full px-3 py-2 rounded-sm text-sm font-mono"
              value={copies} onChange={(e) => setCopies(e.target.value)} data-testid="copies-input" />
          </Field>
          <Field label="Sides">
            <div className="inline-flex rounded-sm border border-[color:var(--ups-border)] overflow-hidden w-full" data-testid="sides-toggle">
              <button onClick={() => setSides(1)} className={`flex-1 py-2 text-xs uppercase tracking-widest font-bold ${sides === 1 ? "bg-[color:var(--ups-brand)] text-black" : ""}`} data-testid="sides-1">1-sided</button>
              <button onClick={() => setSides(2)} className={`flex-1 py-2 text-xs uppercase tracking-widest font-bold ${sides === 2 ? "bg-[color:var(--ups-brand)] text-black" : ""}`} data-testid="sides-2">2-sided</button>
            </div>
          </Field>
          <Field label="Color mode">
            <div className="inline-flex rounded-sm border border-[color:var(--ups-border)] overflow-hidden w-full" data-testid="color-toggle">
              <button onClick={() => setColorMode("bw")} className={`flex-1 py-2 text-xs uppercase tracking-widest font-bold ${colorMode === "bw" ? "bg-[color:var(--ups-brand)] text-black" : ""}`} data-testid="color-bw">B&W</button>
              <button onClick={() => setColorMode("color")} className={`flex-1 py-2 text-xs uppercase tracking-widest font-bold ${colorMode === "color" ? "bg-[color:var(--ups-brand)] text-black" : ""}`} data-testid="color-color">Color</button>
            </div>
          </Field>
          <Field label="Waste sheets" hint="Extra sheets for setup/proofing">
            <input type="number" min={0} className="input-swiss w-full px-3 py-2 rounded-sm text-sm font-mono"
              value={waste} onChange={(e) => setWaste(e.target.value)} data-testid="waste-input" />
          </Field>
        </div>

        <div className="pt-1">
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[color:var(--ups-subtext)]">
            Paper options {lines.length > 1 && <span className="ml-1">({lines.length})</span>}
          </div>
          <div className="text-[10px] text-[color:var(--ups-subtext)] mt-0.5 mb-2">
            Compare multiple papers with different UPs. First row is your primary pick; add more to see side-by-side costs.
          </div>
          <div className="space-y-2">
            {lines.map((line, idx) => (
              <div key={idx} className="grid grid-cols-[1fr_120px_auto] gap-2 items-end" data-testid={`quote-line-${idx}`}>
                <Field label={idx === 0 ? "Primary paper" : `Alternate paper #${idx}`}>
                  <select className="input-swiss w-full px-3 py-2 rounded-sm text-sm" value={line.material_id}
                    onChange={(e) => updateLine(idx, { material_id: e.target.value })}
                    data-testid={`quote-paper-select-${idx}`}>
                    <option value="">— Pick a Cut Sheet paper —</option>
                    {papers.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}{p.size ? ` · ${p.size}` : ""}{p.price_per_sheet > 0 ? ` · ${money4(p.price_per_sheet)}/sheet` : " · (no price)"}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="UPs / sheet" hint="Impressions per parent sheet">
                  <input type="number" min={1} className="input-swiss w-full px-3 py-2 rounded-sm text-sm font-mono"
                    value={line.ups} onChange={(e) => updateLine(idx, { ups: e.target.value })}
                    data-testid={`quote-ups-input-${idx}`} />
                </Field>
                <button onClick={() => removeLine(idx)} disabled={lines.length === 1}
                  className="p-2 rounded-sm border border-[color:var(--ups-border)] hover:bg-white/10 disabled:opacity-30"
                  data-testid={`quote-line-remove-${idx}`}
                  aria-label="Remove line">
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
          <button onClick={addLine} className="mt-3 text-xs uppercase tracking-widest font-bold flex items-center gap-1.5 hover:text-[color:var(--ups-brand)]" data-testid="quote-add-line">
            <Plus size={13}/> Add another paper to compare
          </button>
        </div>

        <div className="flex justify-end pt-2">
          <button disabled={!canCalculate || calculating} onClick={calculate}
            className="btn-primary px-5 py-2.5 text-xs font-bold uppercase tracking-[0.18em] flex items-center gap-1.5 rounded-sm disabled:opacity-40"
            data-testid="calculate-btn">
            {calculating ? <Loader2 size={14} className="animate-spin"/> : <Calculator size={14}/>} Calculate cost
          </button>
        </div>
      </div>

      {/* Result */}
      {result && (
        <div className="card-swiss rounded-sm p-4 space-y-3" data-testid="quote-result-card">
          <div className="flex items-baseline justify-between">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.18em] text-[color:var(--ups-subtext)]">Cost breakdown</div>
              <div className="text-xs text-[color:var(--ups-subtext)] mt-1">
                {result.copies} copies · {result.sides === 2 ? "2-sided" : "1-sided"} ·{" "}
                {result.color_mode === "color" ? "Color" : "B&W"} @ {money4(result.click_rate_applied)}/click
                {result.waste_sheets > 0 ? ` · +${result.waste_sheets} waste` : ""}
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[720px]" data-testid="quote-result-table">
              <thead className="bg-[color:var(--ups-card-alt)]">
                <tr>
                  <Th>Paper</Th>
                  <Th className="text-right">UPs</Th>
                  <Th className="text-right">Parent sheets</Th>
                  <Th className="text-right">Clicks</Th>
                  <Th className="text-right">Paper cost</Th>
                  <Th className="text-right">Click cost</Th>
                  <Th className="text-right">Total</Th>
                </tr>
              </thead>
              <tbody>
                {result.lines.map((r) => {
                  const isCheapest = r.material_id === result.cheapest_material_id && result.lines.length > 1;
                  return (
                    <tr key={r.material_id} className={`border-t border-[color:var(--ups-border)] ${isCheapest ? "bg-green-500/10" : ""}`} data-testid={`result-row-${r.material_id}`}>
                      <td className="px-3 py-2">
                        <div className="font-medium flex items-center gap-1.5">
                          {isCheapest && <Sparkles size={12} className="text-green-500"/>}
                          {r.paper_name}
                        </div>
                        <div className="text-[10px] text-[color:var(--ups-subtext)]">{r.paper_size} · {money4(r.price_per_sheet)}/sheet</div>
                      </td>
                      <td className="px-3 py-2 text-right font-mono">{r.ups}</td>
                      <td className="px-3 py-2 text-right font-mono">{r.parent_sheets}</td>
                      <td className="px-3 py-2 text-right font-mono">{r.clicks}</td>
                      <td className="px-3 py-2 text-right font-mono">{money(r.paper_cost)}</td>
                      <td className="px-3 py-2 text-right font-mono">{money(r.click_cost)}</td>
                      <td className="px-3 py-2 text-right font-bold text-base font-mono" data-testid={`result-total-${r.material_id}`}>
                        {money(r.total_cost)}
                        {isCheapest && <div className="text-[9px] font-bold uppercase tracking-widest text-green-500">Cheapest</div>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="text-[10px] text-[color:var(--ups-subtext)] pt-1">
            <b>Per copy:</b> {result.lines.map((r) => `${r.paper_size || r.paper_name.slice(0,10)}: ${money((r.total_cost || 0) / (result.copies || 1))}`).join(" · ")}
          </div>
        </div>
      )}
    </div>
  );
};

const Th = ({ children, className = "" }) => (
  <th className={`px-3 py-2 text-left text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)] ${className}`}>{children}</th>
);

export default InternalQuoteTab;
